import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Lock } from "lucide-react";
import { ADMIN_TOKEN_KEY, readAdminToken } from "@/components/admin/AdminGuard";
import AdminLangToggle from "@/components/admin/AdminLangToggle";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin — Private unlock screen for the site owner.
 *
 * Uses the existing ADMIN_TOKEN mechanism (validated server-side on every
 * admin API call). No accounts, no registration, no email/password — just
 * a single token field. On success we probe /api/admin/journal/articles
 * to reject bad tokens before redirecting.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useT();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Already unlocked? go straight to the dashboard.
  useEffect(() => {
    if (readAdminToken()) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e && e.preventDefault();
    const token = value.trim();
    if (!token) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/journal/articles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        setErr(t("admin.login.invalid"));
        return;
      }
      if (!res.ok) {
        setErr(t("admin.login.serverError", { status: res.status }));
        return;
      }
      try { localStorage.setItem(ADMIN_TOKEN_KEY, token); } catch { /* ignore */ }
      navigate("/admin/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2.message || t("admin.login.networkError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#050505] text-bone flex items-center justify-center px-6 py-20"
      data-testid="admin-login-screen"
    >
      {/* Faint gold halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0"
        style={{
          background:
            "radial-gradient(600px 400px at 50% 30%, rgba(200,169,106,0.10), transparent 70%)",
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            data-testid="founder-back-home"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> {t("admin.nav.backHome")}
          </Link>
          <AdminLangToggle testId="admin-login-lang-toggle" />
        </div>

        {/* Brand block — the Founder Portal is not a separate site, it is
            the private entrance into StoicOs.ai. */}
        <p
          className="font-serif text-[13px] tracking-[0.32em] uppercase text-gold mb-3"
          data-testid="founder-brand"
        >
          {t("admin.login.brand")}
        </p>
        <h1
          className="font-serif text-4xl md:text-5xl text-bone leading-[1.1] mb-4 inline-flex items-center gap-3 md:gap-4"
          data-testid="founder-portal-title"
        >
          <Lock size={28} strokeWidth={1.4} className="text-gold flex-shrink-0" aria-hidden />
          {t("admin.login.title")}
        </h1>
        <p
          className="text-sm md:text-base text-bone/60 max-w-sm mb-10 leading-relaxed"
          data-testid="founder-portal-sub"
        >
          {t("admin.login.subtitle")}
        </p>

        <form onSubmit={submit}>
          <label className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold mb-3">
            <KeyRound size={12} strokeWidth={1.5} /> {t("admin.login.tokenLabel")}
          </label>
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="admin-login-input"
            autoFocus
            autoComplete="current-password"
            spellCheck={false}
            className="w-full bg-black border border-gold/40 text-bone px-4 py-3 focus:outline-none focus:border-gold"
            placeholder={t("admin.login.tokenPlaceholder")}
          />
          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid="admin-login-submit"
            className="mt-6 w-full bg-gold text-ink px-6 py-3 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-gold-hover transition-colors disabled:opacity-50"
          >
            {busy ? t("admin.label.verifying") : t("admin.action.enter")}
          </button>
          {err && (
            <p className="mt-5 text-sm text-red-400" data-testid="admin-login-error">
              {err}
            </p>
          )}
        </form>

        <p className="mt-12 text-[10px] tracking-[0.24em] uppercase text-fog/50">
          {t("admin.label.founderOnlyAccess")}
        </p>
      </div>
    </main>
  );
}
